import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import {
  Loader2, Shield, Plus, Search, Pencil, Trash2, X, Crown, Users,
  Image as ImageIcon, Palette, BookOpen, Info as InfoIcon, AlertTriangle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { saveHermandadSettings, adminCreateHermandad, adminDeleteHermandad, searchUsersByNickname, getDiasSS, adminSetHermandadDiaSS } from '@/lib/actions';
import { JartinetHermandad, DiaSS, DiaSSHermandadDetalles } from '@/lib/types';

const TIPOS: JartinetHermandad['tipo'][] = ['Hermandad', 'Grupo Parroquial', 'Hermandad de Gloria', 'Pro-Hermandad'];
const DIA_NONE = '__none__';
type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

/**
 * Días del programa de Semana Santa. El "día de salida" de una hermandad ES su
 * día en el programa: aquí no hay lista fija, se leen de `dias_ss`.
 */
function useDiasSS() {
  const [dias, setDias] = useState<DiaSS[]>([]);
  useEffect(() => {
    let cancelled = false;
    getDiasSS().then((r) => {
      if (!cancelled) setDias((r.dias || []).sort((a, b) => a.orden - b.orden));
    });
    return () => { cancelled = true; };
  }, []);
  return dias;
}

/** Id del día del programa en el que está programada una hermandad (o null). */
function diaIdDeHermandad(dias: DiaSS[], hermandadId: string): string | null {
  for (const d of dias) {
    const arr = Array.isArray(d.hermandades) ? d.hermandades : [];
    const found = arr.some((h: any) => (typeof h === 'string' ? h : (h as DiaSSHermandadDetalles)?.hermandadId) === hermandadId);
    if (found) return d.id;
  }
  return null;
}

function useUserNames(uids: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const missing = uids.filter((u) => !names[u]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const fn = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(functions, 'getPublicUserNames');
        const res = await fn({ uids: missing });
        setNames((prev) => ({ ...prev, ...(res.data?.names || {}) }));
      } catch (e) { console.error('Error resolviendo nombres:', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uids.join(',')]);
  return names;
}

/* ── Buscador + lista de miembros ── */
function MemberManager({ icon: Icon, title, accent, uids, names, onAdd, onRemove, busyUid }: {
  icon: React.ElementType; title: string; accent: string;
  uids: string[]; names: Record<string, string>;
  onAdd: (u: FoundUser) => void; onRemove: (uid: string) => void; busyUid: string | null;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(q);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h4 className="font-semibold">{title}</h4>
        <span className="ml-auto text-xs text-muted-foreground">{uids.length}</span>
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario para añadir..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <AnimatePresence>
        {results.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-3 space-y-1 overflow-hidden">
            {results.map((u) => {
              const already = uids.includes(u.id);
              return (
                <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                  <span className="flex-1 truncate text-sm">{u.nickname}</span>
                  <Button size="sm" variant={already ? 'ghost' : 'secondary'} disabled={already || busyUid === u.id} onClick={() => onAdd(u)}>
                    {busyUid === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : already ? 'Ya está' : 'Añadir'}
                  </Button>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {uids.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">Sin miembros.</p>
      ) : (
        <div className="space-y-1">
          {uids.map((uid) => (
            <div key={uid} className="flex items-center gap-2 rounded-xl border border-white/6 bg-muted/20 p-2">
              <Avatar className="h-8 w-8"><AvatarFallback>{(names[uid] || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
              <span className="flex-1 truncate text-sm">{names[uid] || uid}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" disabled={busyUid === uid} onClick={() => onRemove(uid)}>
                {busyUid === uid ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Editor completo de una hermandad ── */
function HermandadEditor({ hermandad, onClose, onChanged }: { hermandad: JartinetHermandad; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState(() => buildForm(hermandad));
  const [saving, setSaving] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Los miembros se llevan en estado LOCAL: el prop `hermandad` es estable y no
  // refleja altas/bajas hechas aquí, así que mantenemos la lista al día localmente.
  const [hermanosMayores, setHermanosMayores] = useState<string[]>(hermandad.hermanosMayores || []);
  const [hermanos, setHermanos] = useState<string[]>(hermandad.hermanos || []);

  // El día de salida sale del programa de Semana Santa y se guarda allí.
  const dias = useDiasSS();
  const [diaId, setDiaId] = useState<string>(DIA_NONE);
  const [diaIdOriginal, setDiaIdOriginal] = useState<string>(DIA_NONE);
  useEffect(() => {
    if (!dias.length) return;
    const actual = diaIdDeHermandad(dias, hermandad.id) || DIA_NONE;
    setDiaId(actual);
    setDiaIdOriginal(actual);
  }, [dias, hermandad.id]);

  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const allMemberUids = useMemo(
    () => Array.from(new Set([...hermanosMayores, ...hermanos])),
    [hermanosMayores, hermanos],
  );
  const names = useUserNames(allMemberUids);

  const handleSaveInfo = async () => {
    setSaving(true);
    const updates: Partial<JartinetHermandad> = {
      nombre: form.nombre.trim(),
      nombreCompleto: form.nombreCompleto.trim(),
      escudoUrl: form.escudoUrl.trim(),
      foto: form.foto.trim(),
      libroReglasUrl: form.libroReglasUrl.trim(),
      titulares: form.titulares.trim(),
      sede: form.sede.trim(),
      fundacion: Number(form.fundacion) || 0,
      tipo: form.tipo as JartinetHermandad['tipo'],
      colorPrimario: form.colorPrimario,
      colorSecundario: form.colorSecundario,
      info: form.info,
      bio: form.bio,
      titulos: form.titulos.split(',').map((s) => s.trim()).filter(Boolean),
    };
    // `dia` no se manda aquí: lo escribe `admin_setHermandadDiaSS` a la vez que
    // coloca la hermandad en su día del programa (los dos lados en sync).
    const result = await saveHermandadSettings(hermandad.id, updates);

    let diaOk = true;
    if (diaId !== diaIdOriginal) {
      const r = await adminSetHermandadDiaSS(hermandad.id, diaId === DIA_NONE ? null : diaId);
      diaOk = r.success;
      if (r.success) setDiaIdOriginal(diaId);
      else toast({ title: 'Error con el día de salida', description: r.message, variant: 'destructive' });
    }

    if (result.success && diaOk) { toast({ title: 'Guardado', description: 'Información actualizada.' }); onChanged(); }
    else if (!result.success) toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setSaving(false);
  };

  const memberOp = async (field: 'hermanos' | 'hermanosMayores', uid: string, method: 'arrayUnion' | 'arrayRemove', okMsg: string) => {
    setBusyUid(uid);
    const result = await saveHermandadSettings(hermandad.id, { [field]: { _method: method, elements: [uid] } } as any);
    if (result.success) {
      // Actualiza el estado local de inmediato (no dependemos del refetch para la UI).
      const setter = field === 'hermanosMayores' ? setHermanosMayores : setHermanos;
      setter((prev) => method === 'arrayUnion' ? Array.from(new Set([...prev, uid])) : prev.filter((u) => u !== uid));
      toast({ title: 'Hecho', description: okMsg });
      onChanged(); // refresca la parrilla en segundo plano (no afecta al editor)
    } else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setBusyUid(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const result = await adminDeleteHermandad(hermandad.id);
    if (result.success) { toast({ title: 'Eliminada', description: result.message }); onChanged(); onClose(); }
    else { toast({ title: 'Error', description: result.message, variant: 'destructive' }); setDeleting(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-white/8">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 border border-white/10"><AvatarImage src={form.escudoUrl} /><AvatarFallback>{form.nombre?.charAt(0)}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <SheetTitle className="truncate">{form.nombre || 'Hermandad'}</SheetTitle>
              <SheetDescription className="truncate">{form.nombreCompleto}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="info" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 mt-3 grid grid-cols-2">
            <TabsTrigger value="info" className="gap-1.5"><InfoIcon className="h-4 w-4" /> Información</TabsTrigger>
            <TabsTrigger value="members" className="gap-1.5"><Users className="h-4 w-4" /> Miembros</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 min-h-0">
            {/* INFORMACIÓN */}
            <TabsContent value="info" className="px-6 py-4 space-y-5 mt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Nombre"><Input value={form.nombre} onChange={(e) => set('nombre', e.target.value)} /></Field>
                <Field label="Nombre completo"><Input value={form.nombreCompleto} onChange={(e) => set('nombreCompleto', e.target.value)} /></Field>
              </div>

              <Field label="Titulares (imágenes/titulares)"><Input value={form.titulares} onChange={(e) => set('titulares', e.target.value)} /></Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Sede"><Input value={form.sede} onChange={(e) => set('sede', e.target.value)} /></Field>
                <Field label="Año de fundación"><Input type="number" value={form.fundacion} onChange={(e) => set('fundacion', e.target.value)} /></Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Tipo de hermandad">
                  <Select value={form.tipo} onValueChange={(v) => set('tipo', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Día de salida (programa de Semana Santa)">
                  <Select value={diaId} onValueChange={setDiaId} disabled={dias.length === 0}>
                    <SelectTrigger><SelectValue placeholder={dias.length ? 'Ninguno' : 'Cargando días…'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={DIA_NONE}>Ninguno</SelectItem>
                      {dias.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.nombredia}{d.tipo === 'visperas' ? ' · vísperas' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[0.65rem] text-muted-foreground">
                    Al guardar, la hermandad se coloca en ese día del programa de Semana Santa.
                    {diaId !== diaIdOriginal && diaIdOriginal !== DIA_NONE && ' Al cambiar de día se descarta su recorrido.'}
                  </p>
                </Field>
              </div>

              <Field label="Títulos (separados por comas)">
                <Input value={form.titulos} onChange={(e) => set('titulos', e.target.value)} placeholder="Pontificia, Real, Ilustre..." />
              </Field>

              {/* Imágenes */}
              <div className="space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><ImageIcon className="h-4 w-4" /> Imágenes</p>
                <Field label="URL del escudo">
                  <div className="flex items-center gap-3">
                    <Input value={form.escudoUrl} onChange={(e) => set('escudoUrl', e.target.value)} placeholder="https://..." />
                    {form.escudoUrl && <img src={form.escudoUrl} alt="" className="h-10 w-10 rounded-lg object-cover border border-white/10 flex-shrink-0" />}
                  </div>
                </Field>
                <Field label="Foto de fondo de la tarjeta">
                  <div className="flex items-center gap-3">
                    <Input value={form.foto} onChange={(e) => set('foto', e.target.value)} placeholder="https://..." />
                    {form.foto && <img src={form.foto} alt="" className="h-10 w-16 rounded-lg object-cover border border-white/10 flex-shrink-0" />}
                  </div>
                </Field>
              </div>

              <Field label={<span className="flex items-center gap-2"><BookOpen className="h-4 w-4" /> Libro de reglas (URL PDF)</span>}>
                <Input value={form.libroReglasUrl} onChange={(e) => set('libroReglasUrl', e.target.value)} placeholder="https://..." />
              </Field>

              {/* Colores */}
              <div className="space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><Palette className="h-4 w-4" /> Colores corporativos</p>
                <div className="flex gap-6">
                  <Field label="Primario"><Input type="color" value={form.colorPrimario} onChange={(e) => set('colorPrimario', e.target.value)} className="h-11 w-20 p-1" /></Field>
                  <Field label="Secundario"><Input type="color" value={form.colorSecundario} onChange={(e) => set('colorSecundario', e.target.value)} className="h-11 w-20 p-1" /></Field>
                </div>
              </div>

              <Field label="Información"><RichTextarea rows={4} value={form.info} onChange={(e) => set('info', e.target.value)} /></Field>
              <Field label="Biografía"><RichTextarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} maxLength={300} /></Field>

              <Button onClick={handleSaveInfo} disabled={saving} className="w-full">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar cambios
              </Button>

              {/* Zona peligrosa */}
              <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-destructive mb-1"><AlertTriangle className="h-4 w-4" /> Zona peligrosa</p>
                <p className="text-xs text-muted-foreground mb-3">Eliminar la hermandad es permanente y no se puede deshacer.</p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={deleting} className="gap-2">
                      {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Eliminar hermandad
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar "{form.nombre}"?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción es permanente. Se borrará la hermandad de forma definitiva.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </TabsContent>

            {/* MIEMBROS */}
            <TabsContent value="members" className="px-6 py-4 space-y-4 mt-0">
              <MemberManager
                icon={Crown} title="Hermanos Mayores" accent="#fbbf24"
                uids={hermanosMayores} names={names} busyUid={busyUid}
                onAdd={(u) => memberOp('hermanosMayores', u.id, 'arrayUnion', `${u.nickname} es ahora Hermano Mayor.`)}
                onRemove={(uid) => memberOp('hermanosMayores', uid, 'arrayRemove', 'Hermano Mayor eliminado.')}
              />
              <MemberManager
                icon={Users} title="Hermanos" accent="#60a5fa"
                uids={hermanos} names={names} busyUid={busyUid}
                onAdd={(u) => memberOp('hermanos', u.id, 'arrayUnion', `${u.nickname} añadido como hermano.`)}
                onRemove={(uid) => memberOp('hermanos', uid, 'arrayRemove', 'Hermano eliminado.')}
              />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function buildForm(h: JartinetHermandad) {
  return {
    nombre: h.nombre || '',
    nombreCompleto: h.nombreCompleto || '',
    escudoUrl: h.escudoUrl || '',
    foto: h.foto || '',
    libroReglasUrl: h.libroReglasUrl || '',
    titulares: h.titulares || '',
    sede: h.sede || '',
    fundacion: String(h.fundacion ?? ''),
    tipo: h.tipo || 'Hermandad',
    colorPrimario: h.colorPrimario || '#9b8854',
    colorSecundario: h.colorSecundario || '#000000',
    info: h.info || '',
    bio: h.bio || '',
    titulos: (h.titulos || []).join(', '),
  };
}

/* ── Diálogo de creación ── */
function CreateHermandadDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nombre, setNombre] = useState('');
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [tipo, setTipo] = useState<string>('Hermandad');
  const [sede, setSede] = useState('');
  const [escudoUrl, setEscudoUrl] = useState('');

  const handleCreate = async () => {
    if (!nombre.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setCreating(true);
    const result = await adminCreateHermandad({
      nombre: nombre.trim(),
      nombreCompleto: (nombreCompleto.trim() || nombre.trim()),
      tipo: tipo as JartinetHermandad['tipo'],
      sede: sede.trim(),
      escudoUrl: escudoUrl.trim() || undefined,
    });
    if (result.success) {
      toast({ title: 'Hermandad creada', description: result.message });
      setNombre(''); setNombreCompleto(''); setSede(''); setEscudoUrl(''); setTipo('Hermandad');
      setOpen(false);
      onCreated();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2"><Plus className="h-4 w-4" /> Crear hermandad</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nueva hermandad</DialogTitle>
          <DialogDescription>Crea la hermandad con lo básico; luego podrás editar todo lo demás.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre *"><Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej: La Macarena" /></Field>
          <Field label="Nombre completo"><Input value={nombreCompleto} onChange={(e) => setNombreCompleto(e.target.value)} placeholder="Real e Ilustre Hermandad..." /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              <Select value={tipo} onValueChange={setTipo}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Sede"><Input value={sede} onChange={(e) => setSede(e.target.value)} /></Field>
          </div>
          <Field label="URL del escudo (opcional)"><Input value={escudoUrl} onChange={(e) => setEscudoUrl(e.target.value)} placeholder="https://..." /></Field>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost" type="button">Cancelar</Button></DialogClose>
          <Button onClick={handleCreate} disabled={creating}>{creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Página ── */
export default function AdminHermandadesPage() {
  const { userData, loading, jartinetData, refetch } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  // Guardamos el objeto completo (no solo el id): así el editor NO se re-deriva de
  // `jartinetData` en cada `refetch()`. Un refetch en segundo plano ya no puede
  // desmontar el Sheet ni "sacarte" de la hermandad; el editor gestiona sus datos.
  const [editing, setEditing] = useState<JartinetHermandad | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  const hermandades = useMemo(() => {
    const list = [...(jartinetData?.hermandades || [])].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((h) => `${h.nombre} ${h.nombreCompleto} ${h.sede}`.toLowerCase().includes(q));
  }, [jartinetData, search]);

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-amber-500/15 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-amber-500/40 blur-xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-amber-400/30 bg-amber-500/15 text-amber-300"><Shield className="h-9 w-9" /></div>
            </div>
            <div>
              <h1 className="font-headline text-4xl font-bold text-foreground md:text-5xl">Gestión de Hermandades</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">Edita toda la información, gestiona hermanos y hermanos mayores, crea o elimina hermandades.</p>
            </div>
          </div>
          <CreateHermandadDialog onCreated={refetch} />
        </div>
      </motion.div>

      {/* Buscador */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar hermandad..." className="pl-9" />
      </div>

      {/* Grid */}
      {hermandades.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
          <Shield className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">No hay hermandades{search ? ' que coincidan con la búsqueda' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {hermandades.map((h) => (
            <motion.button
              key={h.id}
              onClick={() => setEditing(h)}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-amber-400/30"
            >
              <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${h.colorPrimario || '#9b8854'}, ${h.colorSecundario || '#000'})` }} />
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border border-white/10"><AvatarImage src={h.escudoUrl} /><AvatarFallback>{h.nombre?.charAt(0)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{h.nombre}</p>
                  <p className="text-xs text-muted-foreground truncate">{h.tipo}{h.sede ? ` · ${h.sede}` : ''}</p>
                  <p className="mt-1 text-[0.65rem] text-muted-foreground flex items-center gap-2">
                    <span className="flex items-center gap-0.5"><Crown className="h-3 w-3" />{(h.hermanosMayores || []).length}</span>
                    <span className="flex items-center gap-0.5"><Users className="h-3 w-3" />{(h.hermanos || []).length}</span>
                  </p>
                </div>
                <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {editing && <HermandadEditor key={editing.id} hermandad={editing} onClose={() => setEditing(null)} onChanged={refetch} />}
    </div>
  );
}
